import React, { useEffect, useState } from "react";
import { DollarSign, TrendingUp, Users, RefreshCw } from "lucide-react";
import {
  fetchAccountingSummary,
  fmtUsd,
  type AccountingSummary,
} from "@/services/accountingService";

function StatBox({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: "profit" | "neutral";
}) {
  const profitClass =
    highlight === "profit"
      ? "text-emerald-400"
      : "text-foreground";
  return (
    <div className="rounded-lg border border-primary/20 bg-black/30 p-4">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <p className={`text-2xl font-semibold ${profitClass}`}>{value}</p>
    </div>
  );
}

export default function AccountingPanel() {
  const [data, setData] = useState<AccountingSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const summary = await fetchAccountingSummary();
      setData(summary);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, []);

  if (loading && !data) {
    return (
      <div className="rounded-lg border border-primary/20 bg-black/30 p-6">
        <p className="text-muted-foreground">Učitavanje troškova prevoda...</p>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="rounded-lg border border-red-500/30 bg-black/30 p-6">
        <p className="text-red-400">Greška: {error}</p>
        <button
          onClick={load}
          className="mt-3 text-sm text-primary hover:underline"
        >
          Pokušaj ponovo
        </button>
      </div>
    );
  }

  if (!data) return null;

  const todayProfit = data.today.profit;
  const monthProfit = data.month.profit;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg flex items-center gap-2 text-foreground/90">
          <DollarSign className="h-5 w-5 text-primary" />
          Prevodi — Troškovi i Zarada
        </h3>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Osvježi
        </button>
      </div>

      <section>
        <h4 className="text-sm text-muted-foreground mb-3">Danas</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatBox label="Trošak (provider)" value={fmtUsd(data.today.cost)} />
          <StatBox label="Cijena (korisnik)" value={fmtUsd(data.today.price)} />
          <StatBox
            label="PROFIT"
            value={fmtUsd(todayProfit)}
            highlight="profit"
          />
          <StatBox
            label="Charge-ova"
            value={String(data.today.charge_count)}
          />
        </div>
      </section>

      <section>
        <h4 className="text-sm text-muted-foreground mb-3">Ovaj mjesec</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatBox label="Trošak" value={fmtUsd(data.month.cost)} />
          <StatBox label="Cijena" value={fmtUsd(data.month.price)} />
          <StatBox
            label="PROFIT"
            value={fmtUsd(monthProfit)}
            highlight="profit"
          />
          <StatBox
            label="Charge-ova"
            value={String(data.month.charge_count)}
          />
        </div>
      </section>

      <section className="grid md:grid-cols-2 gap-4">
        <div>
          <h4 className="text-sm text-muted-foreground mb-3 flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Po provideru (danas)
          </h4>
          <div className="rounded-lg border border-primary/20 bg-black/30 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-primary/20">
                  <th className="text-left p-2">Provider</th>
                  <th className="text-right p-2">Trošak</th>
                  <th className="text-right p-2">Broj</th>
                </tr>
              </thead>
              <tbody>
                {data.by_provider.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="p-4 text-muted-foreground">
                      Nema charge-ova danas
                    </td>
                  </tr>
                ) : (
                  data.by_provider.map((row) => (
                    <tr
                      key={row.provider}
                      className="border-b border-primary/10"
                    >
                      <td className="p-2 font-mono text-xs">{row.provider}</td>
                      <td className="p-2 text-right">
                        {fmtUsd(row.cost)}
                      </td>
                      <td className="p-2 text-right">{row.count}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <h4 className="text-sm text-muted-foreground mb-3 flex items-center gap-2">
            <Users className="h-4 w-4" />
            Top korisnici (danas)
          </h4>
          <div className="rounded-lg border border-primary/20 bg-black/30 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-primary/20">
                  <th className="text-left p-2">User</th>
                  <th className="text-right p-2">Potrošnja</th>
                  <th className="text-right p-2">#</th>
                </tr>
              </thead>
              <tbody>
                {data.top_users.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="p-4 text-muted-foreground">
                      Nema podataka
                    </td>
                  </tr>
                ) : (
                  data.top_users.map((row) => (
                    <tr
                      key={row.user_id}
                      className="border-b border-primary/10"
                    >
                      <td className="p-2 font-mono text-xs">{row.user_id}</td>
                      <td className="p-2 text-right">{fmtUsd(row.spend)}</td>
                      <td className="p-2 text-right">{row.charge_count}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="grid md:grid-cols-2 gap-4">
        <div className="rounded-lg border border-primary/20 bg-black/30 p-4">
          <h4 className="text-sm text-muted-foreground mb-2">
            Besplatno vs plaćeno (danas)
          </h4>
          <p className="text-lg">
            <span className="text-emerald-400">
              {data.free_vs_paid.free_pct}%
            </span>{" "}
            besplatno ({data.free_vs_paid.free_count}) ·{" "}
            <span className="text-amber-400">
              {data.free_vs_paid.paid_pct}%
            </span>{" "}
            plaćeno ({data.free_vs_paid.paid_count})
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Naši/arhiva (0 trošak) vs Google/plaćeni provideri
          </p>
        </div>

        <div className="rounded-lg border border-primary/20 bg-black/30 p-4">
          <h4 className="text-sm text-muted-foreground mb-2">Krediti</h4>
          <div className="grid grid-cols-3 gap-2 text-sm">
            <div>
              <p className="text-muted-foreground text-xs">Izdato</p>
              <p>{fmtUsd(data.credits.issued_usd)}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">Potrošeno</p>
              <p>{fmtUsd(data.credits.spent_usd)}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">Saldo ukupno</p>
              <p>{fmtUsd(data.credits.total_balance_usd)}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
